/**
 * Die englische Fassung der Oberfläche.
 *
 * Schlüssel ist der deutsche Satz — Begründung in `t.ts`. Fehlt ein Eintrag,
 * bleibt Deutsch stehen; damit das nicht unbemerkt passiert, prüft
 * `scripts/check-i18n.mjs` bei jedem Lauf, dass jeder `t()`-Aufruf hier steht
 * und dass hier nichts steht, was niemand aufruft.
 *
 * ## Zum Ton
 *
 * Die deutschen Texte sind bewusst Alltagssprache (Anf. 59) — kein
 * Fachvokabular, keine Passivkonstruktionen, und bei Warnungen wird gesagt,
 * was passiert, nicht dass „ein Fehler aufgetreten ist". Die englische Fassung
 * hält das durch: „Only the two of you can read this", nicht „End-to-end
 * encryption enabled".
 *
 * Anreden bleiben im Deutschen beim Du. Im Englischen gibt es die Wahl nicht,
 * dafür wird dort dieselbe Nähe über kurze Sätze erzeugt.
 */

export const EN: Record<string, string> = {
  // --- Hülle, Kopfzeile, Reiter --------------------------------------------
  'Family Hub': 'Family Hub',
  'Suchen': 'Search',
  'Mehr': 'More',
  'Hauptbereiche': 'Main sections',
  'Familie': 'Family',
  'Chats': 'Chats',
  'Anrufe': 'Calls',
  'Standort': 'Location',
  "Wie geht's?": 'How are you?',
  'SOS': 'SOS',
  'SOS — Notfall auslösen': 'SOS — raise the alarm',
  'Zum Inhalt springen': 'Skip to content',
  'Wird geladen…': 'Loading…',

  // --- Bereiche unter „Mehr" ------------------------------------------------
  'Fotos': 'Photos',
  'Dateien': 'Files',
  'Kalender': 'Calendar',
  'Aufgaben': 'Tasks',
  'Notizen': 'Notes',
  'Familie verwalten': 'Manage family',
  'Einstellungen': 'Settings',
  'Meine Geräte': 'My devices',
  'Sicherheit': 'Security',
  'Abmelden': 'Sign out',

  // --- Rollen ---------------------------------------------------------------
  'Administrator': 'Administrator',
  'Elternteil': 'Parent',
  'Erwachsener': 'Adult',
  'Kind': 'Child',
  'Gast': 'Guest',

  // --- Banner ---------------------------------------------------------------
  'Keine Internetverbindung. Du siehst gespeicherte Inhalte.':
    'No internet connection. You are seeing saved content.',
  'Noch nicht gebaut': 'Not built yet',
  'Diesen Bereich gibt es noch nicht. Das Gerüst steht, die Funktion noch nicht.':
    'This section does not exist yet. The frame is there, the function is not.',
  // --- Anmeldung ------------------------------------------------------------
  'Willkommen': 'Welcome',
  'Melde dich an, um deine Familie zu sehen.': 'Sign in to see your family.',
  'Anmelden': 'Sign in',
  'Einen Moment…': 'One moment…',
  'Dein Gerät fragt nach Fingerabdruck, Gesicht oder deiner Geräte-PIN.':
    'Your device will ask for your fingerprint, your face or your device PIN.',
  'Anmelden mit Name und Passwort': 'Sign in with name and password',
  'Dein Name': 'Your name',
  'Passwort': 'Password',
  'Keine Verbindung. Bist du online?': 'No connection. Are you online?',
  'Das hat nicht geklappt. Versuch es noch einmal.': 'That did not work. Please try again.',
  'Zu viele Versuche. Warte kurz und probier es dann noch einmal.':
    'Too many attempts. Wait a moment, then try again.',
  'Abgebrochen. Tippe noch einmal auf „Anmelden".': 'Cancelled. Tap “Sign in” again.',
  'Dieses Gerät unterstützt Passkeys nicht. Nimm den Weg mit Name und Passwort.':
    'This device does not support passkeys. Use name and password instead.',
  'Da ist etwas schiefgelaufen. Versuch es noch einmal.':
    'Something went wrong. Please try again.',
  // --- Darstellung ----------------------------------------------------------
  'Darstellung': 'Appearance',
  'Sprache': 'Language',
  'Ändert die ganze App. Geschriebenes wird zusätzlich übersetzt gezeigt.':
    'Changes the whole app. What people write is also shown translated.',
  'Schriftgröße': 'Text size',
  // --- Startseite -----------------------------------------------------------
  'Deine Familie': 'Your family',
  'Noch niemand da. Lade jemanden ein — unter „Mehr" → „Familie verwalten".':
    'Nobody here yet. Invite someone — under “More” → “Manage family”.',
  'Es läuft gerade ein Anruf.': 'A call is going on right now.',
  'Dazukommen': 'Join',
  'Ein verpasster Anruf': 'One missed call',
  '{n} verpasste Anrufe': '{n} missed calls',
  'ansehen': 'view',
  'Zuletzt gesagt': 'Recently said',
  'Du': 'You',
  'Jemand': 'Someone',
  'Alle anzeigen': 'Show all',
  'Keine Verbindung. Du siehst den letzten Stand.':
    'No connection. You are seeing the last known state.',
  'Die Übersicht konnte nicht geladen werden.': 'The overview could not be loaded.',
  'Wie geht es dir?': 'How are you doing?',
  'Sag deiner Familie kurz Bescheid — mit einem Tipp.':
    'Let your family know — with one tap.',
  'Zurücknehmen': 'Undo',
  'Weniger': 'Less',
  'Alle Möglichkeiten': 'All options',
  'Lieber in eigenen Worten?': 'Rather in your own words?',

  // --- Befindlichkeit (Beschriftungen aus @fh/shared) ------------------------
  'Mir geht es gut': 'I am doing well',
  'Alles okay': 'All okay',
  'Etwas gestresst': 'A bit stressed',
  'Müde': 'Tired',
  'Nicht so gut': 'Not so good',
  'Brauche etwas Unterstützung': 'Could use some support',
  'Hilfe benötigt': 'Need help',

  // --- Zeit und Anwesenheit -------------------------------------------------
  'Jetzt online': 'Online now',
  'Noch nie angemeldet': 'Never signed in',
  'Gerade eben': 'Just now',
  'Vor {n} Min.': '{n} min ago',
  'Vor {n} Std.': '{n} hr ago',
  'Gestern': 'Yesterday',
  'Vor {n} Tagen': '{n} days ago',
  'Länger nicht da': 'Not around for a while',
  'gerade eben': 'just now',
  'vor {n} Min.': '{n} min ago',
  'vor {n} Std.': '{n} hr ago',
  'gestern': 'yesterday',
  'vor {n} Tagen': '{n} days ago',
  'Angemeldet als {wer}.': 'Signed in as {wer}.',
  // --- Wie geht's? ----------------------------------------------------------
  'Was die Familie gesagt hat': 'What the family said',
  'Noch nichts. Sag als Erste·r, wie es dir geht.': 'Nothing yet. Be the first to say how you are.',
  'Konnte nicht geladen werden.': 'Could not be loaded.',
  'Oder sag es in eigenen Worten': 'Or say it in your own words',
  'Bin beim Arzt, melde mich später.': 'At the doctor, will get back to you later.',
  'Text speichern': 'Save text',
  'Einträge verschwinden nach {n} Tagen von selbst. Es gibt keine Auswertung und keine Statistik darüber.':
    'Entries disappear by themselves after {n} days. There is no analysis and no statistics.',
  'Diesen Text kann der Server lesen — er steht ja auch auf der Startseite.':
    'The server can read this text — it is on the home screen too.',
  'Was NUR für eine Person bestimmt ist, schick es durch den Chat: Der ist verschlüsselt.':
    'Anything meant for ONE person only belongs in the chat: that one is encrypted.',

  // --- Suche ----------------------------------------------------------------
  'Wonach suchst du?': 'What are you looking for?',
  'Zahnarzt, Einkaufsliste, Urlaub…': 'Dentist, shopping list, holiday…',
  'Nichts gefunden.': 'Nothing found.',
  'Die Suche hat nicht geklappt.': 'The search did not work.',
  'Termin': 'Event',
  'Aufgabe': 'Task',
  'Notiz': 'Note',
  'Datei': 'File',
  'Foto': 'Photo',
  'Durchsucht werden Termine, Aufgaben, Notizen, Dateien und Fotos.':
    'Events, tasks, notes, files and photos are searched.',
  'Nicht durchsucht:': 'Not searched:',
  'Chat-Nachrichten und verschlüsselte Notizen — die kann der Server nicht lesen. Das ist Absicht.':
    'Chat messages and encrypted notes — the server cannot read those. That is on purpose.',

  // --- Fotos ----------------------------------------------------------------
  'Foto hinzufügen': 'Add photo',
  'Nur Favoriten': 'Favourites only',
  'Alle': 'All',
  'Noch keine Fotos.': 'No photos yet.',
  'Die Fotos konnten nicht geladen werden.': 'The photos could not be loaded.',
  'Foto wird hochgeladen…': 'Uploading photo…',
  'Der Aufnahmeort wird aus dem Bild entfernt, bevor es dein Gerät verlässt.':
    'The location is removed from the picture before it leaves your device.',
  'Favorit entfernen': 'Remove from favourites',
  'Als Favorit merken': 'Mark as favourite',
  'Mich entfernen': 'Remove me',
  // --- SOS ------------------------------------------------------------------
  'Das hier ist ein Familien-Alarm, kein Notruf. Bei Lebensgefahr wähle die 112 — das geht auch ohne Internet.':
    'This is a family alarm, not an emergency call. If life is at risk, dial 112 — that works without internet.',
  '🧪 TEST — keine echte Notlage': '🧪 TEST — not a real emergency',
  'Countdown läuft': 'Countdown running',
  'Alarm läuft': 'Alarm running',
  'Jemand kümmert sich': 'Someone is taking care of it',
  'Erledigt': 'Done',
  'Abgebrochen': 'Cancelled',
  'Fehlalarm': 'False alarm',
  'Notruf (Rettung, Feuerwehr)': 'Emergency (ambulance, fire brigade)',
  'Polizei': 'Police',
  'Euro-Notruf': 'European emergency number',
  'Rettung': 'Ambulance',
  'Sanität': 'Ambulance service',
  'Die Alarme konnten nicht geladen werden.': 'The alarms could not be loaded.',
  'Der Alarm konnte nicht ausgelöst werden. Bitte ruf direkt an.':
    'The alarm could not be raised. Please call directly.',
  'Kein Netz — dein Alarm wird gesendet, sobald Verbindung da ist.':
    'No network — your alarm will be sent as soon as there is a connection.',
  'Ruf jetzt direkt an, wenn es dringend ist.': 'Call directly now if it is urgent.',
  'SOS auslösen: gedrückt halten oder zweimal tippen':
    'Raise SOS: press and hold, or tap twice',
  'Noch einmal tippen': 'Tap once more',
  '{n} Sekunden halten': 'Hold for {n} seconds',
  'Der Alarm geht gleich an deine Familie.': 'The alarm goes to your family in a moment.',
  'Abbrechen': 'Cancel',
  'Dein Alarm läuft': 'Your alarm is running',
  'Deine Familie wurde benachrichtigt. Noch hat niemand geantwortet.':
    'Your family has been notified. Nobody has answered yet.',
  '{wer} kümmert sich.': '{wer} is taking care of it.',
  'Stufe {n} — es wurden weitere Personen benachrichtigt.':
    'Level {n} — more people have been notified.',
  '{wer} braucht Hilfe': '{wer} needs help',
  'Ausgelöst': 'Raised',
  'Stufe {n}': 'Level {n}',
  'Ich kümmere mich': 'I am on it',
  'In der App anrufen': 'Call in the app',
  'Wo ist {wer}?': 'Where is {wer}?',
  '{nummer} anrufen': 'Call {nummer}',
  // --- Anrufe ---------------------------------------------------------------
  'Anrufen': 'Call',
  'Mit Video': 'With video',
  'Unterhaltung': 'Conversation',
  'Zu zweit': 'Just the two of you',
  '{n} Personen': '{n} people',
  'Ein Videoanruf mit mehr als {n} Personen ruckelt auf älteren Geräten. Ein Audio-Anruf klappt auch mit mehr.':
    'A video call with more than {n} people stutters on older devices. An audio call works with more.',
  'Der Anruf konnte nicht gestartet werden.': 'The call could not be started.',
  'Die App darf nicht auf Mikrofon oder Kamera zugreifen. Bitte in den Einstellungen erlauben.':
    'The app is not allowed to use the microphone or camera. Please allow it in the settings.',
  'Der Anruf konnte nicht aufgebaut werden.': 'The call could not be connected.',
  'Es klingelt…': 'Ringing…',
  'Im Gespräch': 'On the call',
  'Dein eigenes Bild': 'Your own picture',
  'Mikrofon an': 'Microphone on',
  'Stumm': 'Muted',
  'Kamera an': 'Camera on',
  'Kamera aus': 'Camera off',
  'Bildschirm teilen': 'Share screen',
  'Freigabe beenden': 'Stop sharing',
  'Die Bildschirmfreigabe hat nicht geklappt.': 'Screen sharing did not work.',
  'Verbindung wird aufgebaut…': 'Connecting…',
  'Letzte Anrufe': 'Recent calls',
  'Verpasst': 'Missed',
  'Eingehend': 'Incoming',
  'Ausgehend': 'Outgoing',
  'Beendet': 'Ended',
  'Abgelehnt': 'Declined',
  'Aufgelegt, bevor jemand ranging': 'Hung up before anyone answered',
  'Verbindung fehlgeschlagen': 'Connection failed',
  'Annehmen': 'Answer',
  'Ablehnen': 'Decline',
  '{wer} ruft an': '{wer} is calling',
  'Videoanruf': 'Video call',
  'Sprachanruf': 'Voice call',
  '{n} Sek.': '{n} sec',
  '{n} Min.': '{n} min',
  '{min} Min. {sek} Sek.': '{min} min {sek} sec',

  // --- Geräte ---------------------------------------------------------------
  'Die Geräteliste konnte nicht geladen werden.': 'The device list could not be loaded.',
  'Das Abmelden hat nicht geklappt.': 'Signing out did not work.',
  'Das Sperren hat nicht geklappt.': 'Blocking did not work.',
  'Das hat nicht geklappt.': 'That did not work.',
  '„{name}" wirklich sperren?': 'Really block “{name}”?',
  'Das Gerät wird abgemeldet und kann sich nicht mehr anmelden. Nutze das, wenn du es verloren hast.':
    'The device is signed out and cannot sign in again. Use this if you have lost it.',
  'Alle anderen Geräte abmelden? Dieses Gerät bleibt angemeldet.':
    'Sign out all other devices? This device stays signed in.',
  'Dieses Gerät': 'This device',
  'Gesperrt': 'Blocked',
  'Unbekannt': 'Unknown',
  'angemeldet': 'signed in',
  'Verloren — sperren': 'Lost — block it',
  'Alle anderen Geräte abmelden': 'Sign out all other devices',
  'noch nie benutzt': 'never used',
  'vor {n} Minuten': '{n} minutes ago',
  'vor {n} Stunden': '{n} hours ago',

  // --- Einladung ------------------------------------------------------------
  'Diese Einladung ist abgelaufen oder wurde schon benutzt. Bitte um eine neue.':
    'This invitation has expired or was already used. Please ask for a new one.',
  'Das hat nicht geklappt. Du kannst es später in den Einstellungen nachholen.':
    'That did not work. You can do it later in the settings.',
  'Du bist eingeladen': 'You are invited',
  'Wie sollen dich die anderen sehen?': 'How should the others see you?',
  'zum Beispiel: Oma': 'for example: Grandma',
  'Weiter': 'Continue',
  'Fast fertig, {name}': 'Almost done, {name}',
  'Damit du dich künftig ohne Passwort anmelden kannst, richten wir jetzt deinen Fingerabdruck oder dein Gesicht ein.':
    'So that you can sign in without a password from now on, let us set up your fingerprint or your face.',
  'Jetzt einrichten': 'Set up now',
  'Später einrichten': 'Set up later',
// --- Dateien (Zone B) -----------------------------------------------------
  'Die Dateien konnten nicht geladen werden.': 'The files could not be loaded.',
  'Wie soll der Ordner heißen?': 'What should the folder be called?',
  'Der Ordner konnte nicht angelegt werden.': 'The folder could not be created.',
  '„{name}" in den Papierkorb legen?': 'Move „{name}" to the bin?',
  'Der Download-Link konnte nicht geholt werden.': 'The download link could not be fetched.',
  'Dateien hier kann der Server lesen — das ist nötig für Vorschau und Suche.':
    'The server can read files kept here — that is needed for previews and search.',
  'Was NUR für einen Menschen bestimmt ist, schick es durch den Chat: Dort ist es verschlüsselt.':
    'Anything meant for one person ONLY: send it through the chat, where it is encrypted.',
  'Ordnerpfad': 'Folder path',
  'Datei hinzufügen': 'Add file',
  'Neuer Ordner': 'New folder',
  'Papierkorb': 'Bin',
  '{name} wird hochgeladen…': 'Uploading {name}…',
  '{n} % — bei schlechter Verbindung macht die App von selbst weiter, sobald sie wieder da ist.':
    '{n} % — on a poor connection the app carries on by itself as soon as it is back.',
  'Hier ist noch nichts. Füge die erste Datei hinzu.': 'Nothing here yet. Add the first file.',
  'Ordner': 'Folder',
  'Fassung {n}': 'Version {n}',
  'Nicht geprüft': 'Not scanned',
  'Geprüft': 'Scanned',
  '„{name}" löschen': 'Delete „{name}"',
  'Der Papierkorb konnte nicht geladen werden.': 'The bin could not be loaded.',
  'Zurück zu den Dateien': 'Back to the files',
  'Gelöschtes bleibt 30 Tage hier und lässt sich zurückholen. Danach ist es endgültig weg.':
    'Deleted items stay here for 30 days and can be brought back. After that they are gone for good.',
  'Der Papierkorb ist leer.': 'The bin is empty.',
  'Zurückholen': 'Bring back',

  // --- Prüfstand einer Datei (aus @fh/shared) -------------------------------
  'Wird geprüft…': 'Being scanned…',
  'Auf Viren geprüft': 'Scanned for viruses',
  'Schadsoftware gefunden — Datei gesperrt': 'Malware found — file blocked',
  'Nicht geprüft: verschlüsselt hochgeladen, der Server kann sie nicht lesen':
    'Not scanned: uploaded encrypted, the server cannot read it',
  'Nicht geprüft: der Virenscanner läuft gerade nicht':
    'Not scanned: the virus scanner is not running right now',

  // --- Standort -------------------------------------------------------------
  'Wo ist wer?': 'Who is where?',
  'Gerade teilt niemand seinen Standort mit dir. Du kannst jemanden fragen — er entscheidet dann selbst.':
    'Nobody is sharing their location with you right now. You can ask someone — they decide for themselves.',
  'Wähle zuerst aus, wer deinen Standort sehen darf.':
    'First choose who may see your location.',
  'Meinen Standort teilen': 'Share my location',
  'Wer darf sehen, wo du bist?': 'Who may see where you are?',
  'Wie genau?': 'How precise?',
  'Bei „Ungefähre Gegend" und „Nur die Stadt" verlässt dein genauer Ort dein Gerät gar nicht erst.':
    'With „Rough area" and „Town only" your exact location never leaves your device in the first place.',
  'Läuft gerade — dein Gerät sendet auf Stufe „{stufe}".':
    'Running now — your device is sending at level „{stufe}".',
  'dieser Person': 'that person',
  'Das ist der letzte bekannte Ort. Solange die App bei {name} geschlossen ist, kommt kein neuer dazu.':
    'This is the last known location. As long as the app is closed on {name}\'s device, no new one arrives.',
  'Ungefähre Angabe': 'Rough position',
  'auf {n} m genau': 'accurate to {n} m',
  'dauerhaft (Sorgerecht)': 'permanent (parental responsibility)',
  'In deiner Karten-App öffnen': 'Open in your maps app',
  'Übersicht: {namen}': 'Overview: {namen}',
  'Breite etwa {mass}': 'About {mass} across',
  '{name} fragt, wo du bist': '{name} is asking where you are',
  'Zeigen für {dauer}': 'Show for {dauer}',
  'Nein': 'No',
  'Wer hat nachgesehen, wo du bist?': 'Who has checked where you are?',
  'Deine Eltern sehen, wo du bist': 'Your parents can see where you are',
  'Das haben deine Eltern eingestellt. Du kannst es nicht selbst ausschalten. Aber zwei Dinge kannst du:':
    'Your parents set this up. You cannot switch it off yourself. But there are two things you can do:',
  'Du siehst weiter unten, wer wann nachgesehen hat.':
    'Further down you can see who looked, and when.',
  'Du kannst sagen, dass du das nicht möchtest — sie bekommen eine Nachricht.':
    'You can say that you do not want this — they will get a message.',
  'Mit 16 schaltet sich das automatisch ab.': 'At 16 this switches off automatically.',
  'Deine Eltern wurden benachrichtigt.': 'Your parents have been notified.',
  'Möchtest du etwas dazu sagen? (freiwillig)': 'Would you like to say something about it? (optional)',
  'Ich möchte das nicht': 'I do not want this',

  // --- Standort-Dauern und -Stufen (aus @fh/shared) -------------------------
  '10 Minuten': '10 minutes',
  '30 Minuten': '30 minutes',
  '1 Stunde': '1 hour',
  'Bis ich es stoppe': 'Until I stop it',
  'Genauer Ort': 'Exact location',
  'Ungefähre Gegend': 'Rough area',
  'Nur die Stadt': 'Town only',

  // --- Meine Daten: Export und Kontolöschung --------------------------------
  'Wird zusammengestellt…': 'Being put together…',
  'Nachrichten werden entschlüsselt…': 'Decrypting messages…',
  'Fertig. Die Datei liegt in deinen Downloads.': 'Done. The file is in your downloads.',
  'Das hat nicht geklappt. Versuch es später noch einmal.':
    'That did not work. Please try again later.',
  'Meine Daten mitnehmen': 'Take my data with me',
  'Eine Datei mit allem, was der Server über dich weiß: dein Konto, deine Geräte, Termine, Aufgaben, Notizen und deine Nachrichten.':
    'One file with everything the server knows about you: your account, your devices, events, tasks, notes and your messages.',
  'Die Nachrichten werden auf diesem Gerät entschlüsselt, bevor die Datei entsteht — der Server kann sie nicht lesen und liefert sie deshalb verschlüsselt aus. Fotos und Dateien sind mit Namen und einem Link aufgeführt; die Bilder selbst sind nicht enthalten.':
    'The messages are decrypted on this device before the file is created — the server cannot read them and hands them over encrypted. Photos and files are listed by name and link; the images themselves are not included.',
  'Nur die Daten (klein)': 'Data only (small)',
  'Alles mit Fotos (ZIP)': 'Everything with photos (ZIP)',
  'Im ZIP sind die Fotos und Dateien wirklich enthalten — die Chats darin bleiben aber verschlüsselt, weil der Server das Archiv packt und sie nicht lesen kann. Beide Wege ergänzen sich; eine LIESMICH-Datei im Archiv erklärt es noch einmal.':
    'The ZIP really does contain the photos and files — but the chats inside stay encrypted, because the server packs the archive and cannot read them. The two ways complement each other; a README file in the archive explains it again.',
  '(auf diesem Gerät nicht entschlüsselbar)': '(cannot be decrypted on this device)',
  'Konto entfernen': 'Remove account',
  'Endgültig. Lade dir vorher deine Daten herunter — danach geht es nicht mehr.':
    'Final. Download your data first — afterwards it is no longer possible.',
  'Konto entfernen…': 'Remove account…',
  'Das verschwindet in jedem Fall:': 'This disappears in any case:',
  '{n} gespeicherte Standorte, dein Statusverlauf': '{n} saved locations, your status history',
  'alle deine Geräte, Anmeldungen und Schlüssel': 'all your devices, sign-ins and keys',
  'deine privaten Notizen': 'your private notes',
  'Was soll mit deinen Beiträgen geschehen?': 'What should happen to what you contributed?',
  '({a} Nachrichten, {b} Fotos, {c} Termine, {d} Aufgaben, {e} Dateien)':
    '({a} messages, {b} photos, {c} events, {d} tasks, {e} files)',
  'Behalten': 'Keep',
  'Bleiben in der Familie stehen, ohne deinen Namen. Der Chatverlauf der anderen bleibt lesbar.':
    'They stay with the family, without your name. The others can still read the chat history.',
  'Auch löschen': 'Delete them too',
  'Deine Nachrichten, Fotos, Termine und Dateien verschwinden für alle. Im Verlauf der anderen bleiben Lücken.':
    'Your messages, photos, events and files disappear for everyone. Gaps stay in the others\' history.',
  'Zum Bestätigen deinen Namen eintippen:': 'Type your name to confirm:',
  'Wird entfernt…': 'Removing…',
  'Endgültig entfernen': 'Remove for good',

  // --- Chat -----------------------------------------------------------------
  'Noch keine Unterhaltung. Wähle unten jemanden aus.':
    'No conversation yet. Pick someone below.',
  'Alle in der Familie': 'Everyone in the family',
  'Direktnachricht': 'Direct message',
  'neue Nachricht': 'new message',
  'neue Nachrichten': 'new messages',
  'Nur ihr könnt das lesen.': 'Only the two of you can read this.',
  'Diese Nachrichten sind Ende-zu-Ende verschlüsselt — auch der Server kann sie nicht öffnen.':
    'These messages are end-to-end encrypted — not even the server can open them.',
  'Familienchat': 'Family chat',
  'Diese Unterhaltung gibt es nicht mehr.': 'This conversation no longer exists.',
  'Ende-zu-Ende verschlüsselt': 'End-to-end encrypted',
  'Zurück': 'Back',
  'Entfernen': 'Remove',
  'Nachricht gelöscht.': 'Message deleted.',
  'Rückgängig': 'Undo',
  'Ältere Nachrichten laden': 'Load older messages',
  '{name} schreibt…': '{name} is typing…',
  'Gelöschtes Mitglied': 'Deleted member',
  'bearbeitet': 'edited',
  'Aktionen für diese Nachricht': 'Actions for this message',
  'Antworten': 'Reply',
  'Löschen': 'Delete',
  'Die Angaben zu dieser Datei fehlen.': 'The details of this file are missing.',
  'Das Bild konnte nicht geladen werden.': 'The image could not be loaded.',
  'Video': 'Video',
  'Tonaufnahme': 'Voice recording',
  '{art} abspielen{groesse}': 'Play {art}{groesse}',
  'Foto anzeigen{groesse}': 'Show photo{groesse}',
  'Tippen für das große Bild': 'Tap for the large image',
  'Dieses Video kann Aufnahmeort und -zeit enthalten.':
    'This video may contain where and when it was recorded.',
  'Bei Fotos entfernt Family Hub das; bei Videos geht es nicht. Alle in dieser Unterhaltung könnten also sehen, wo es aufgenommen wurde.':
    'For photos Family Hub removes that; for videos it cannot. So everyone in this conversation could see where it was recorded.',
  'Trotzdem senden?': 'Send anyway?',
  'Der Anhang konnte nicht vorbereitet werden.': 'The attachment could not be prepared.',
  'Antwort auf eine Nachricht': 'Reply to a message',
  '1 Anhang: {name}': '1 attachment: {name}',
  '{n} Anhänge': '{n} attachments',
  'Anhang wird verschlüsselt und hochgeladen…': 'Encrypting and uploading the attachment…',
  'Foto, Video oder Ton anhängen': 'Attach a photo, video or sound',
  'Nachricht': 'Message',
  'Nachricht schreiben…': 'Write a message…',
  'Senden': 'Send',
  'Neue Unterhaltung': 'New conversation',
  'Deine Nachrichten sind gesperrt. Melde dich neu an, um sie zu entsperren.':
    'Your messages are locked. Sign in again to unlock them.',

  // --- Mitglieder verwalten -------------------------------------------------
  'Verwaltet die Familie, Rollen und Geräte': 'Manages the family, roles and devices',
  'Darf einladen und Kinder-Geräte verwalten': 'May invite people and manage children\'s devices',
  'Voller Zugriff auf die eigenen Sachen': 'Full access to their own things',
  'Chat, Anrufe, SOS — keine Verwaltung': 'Chat, calls, SOS — no administration',
  'Nur die Räume, in die er eingeladen wurde': 'Only the rooms they were invited to',
  'Die Mitgliederliste konnte nicht geladen werden.': 'The member list could not be loaded.',
  'Das ist der letzte Administrator — die Rolle kann nicht geändert werden.':
    'This is the last administrator — the role cannot be changed.',
  'Die Rolle konnte nicht geändert werden.': 'The role could not be changed.',
  '„{name}" wirklich entfernen?': 'Really remove „{name}"?',
  'Das Konto wird deaktiviert, alle Geräte werden gesperrt. Bereits gesendete Nachrichten bleiben erhalten.':
    'The account is deactivated and all devices are blocked. Messages already sent are kept.',
  'Das lässt sich rückgängig machen: In der Liste steht danach „Wieder aktivieren".':
    'This can be undone: the list then shows „Reactivate".',
  'Das ist der letzte Administrator und kann nicht entfernt werden.':
    'This is the last administrator and cannot be removed.',
  'Das Entfernen hat nicht geklappt.': 'Removing did not work.',
  'Den Namen „{name}" trägt inzwischen jemand anderes. Erst diese Person umbenennen, dann noch einmal versuchen.':
    'Somebody else now goes by the name „{name}". Rename that person first, then try again.',
  'Dieses Konto wurde endgültig entfernt und kann nicht zurückgeholt werden.':
    'This account was removed for good and cannot be brought back.',
  'Das Aktivieren hat nicht geklappt.': 'Reactivating did not work.',
  'Die Vorschau konnte nicht geladen werden.': 'The preview could not be loaded.',
  'Das Konto muss zuerst deaktiviert werden.': 'The account has to be deactivated first.',
  'Das endgültige Entfernen hat nicht geklappt.': 'Removing for good did not work.',
  'Deaktiviert': 'Deactivated',
  '{n} Rechte': '{n} permissions',
  'Rolle': 'Role',
  'Wieder aktivieren': 'Reactivate',
  'Was bedeuten die Rollen?': 'What do the roles mean?',
  'Nachrichten': 'Messages',
  'Termine': 'Events',
  'Standortpunkte': 'Location points',
  '„{name}" endgültig entfernen': 'Remove „{name}" for good',
  'Das lässt sich NICHT rückgängig machen. Geräte, Schlüssel, Sitzungen, Standortdaten und private Notizen werden gelöscht. Der Name verschwindet aus der Familie.':
    'This can NOT be undone. Devices, keys, sessions, location data and private notes are deleted. The name disappears from the family.',
  'Dieses Konto hat nichts beigetragen — es gibt keine Nachrichten, Fotos oder Termine, über die zu entscheiden wäre.':
    'This account contributed nothing — there are no messages, photos or events to decide about.',
  'Beigetragen hat dieses Konto:': 'This account contributed:',
  'Was soll damit geschehen? Beides ist vertretbar — für die einen ist der Verlauf eines Menschen das, was von ihm bleibt, für die anderen sollen die eigenen Worte mitgehen.':
    'What should happen to it? Both are defensible — for some, what a person wrote is what remains of them; for others, their own words should leave with them.',
  'Entfernen, Beiträge behalten': 'Remove, keep contributions',
  'Entfernen, Beiträge mit löschen': 'Remove and delete contributions too',
  'Diese Rolle darfst du nicht vergeben.': 'You are not allowed to assign this role.',
  'Die Einladung konnte nicht erstellt werden.': 'The invitation could not be created.',
  'Jemanden einladen': 'Invite someone',
  'Name (optional)': 'Name (optional)',
  'Als': 'As',
  'Einladung erstellen': 'Create invitation',
  'Diesen Link weitergeben.': 'Pass this link on.',
  'Er gilt 72 Stunden und funktioniert genau einmal. Danach ist er nicht mehr abrufbar — gespeichert ist nur seine Prüfsumme.':
    'It is valid for 72 hours and works exactly once. After that it cannot be retrieved — only its checksum is stored.',
  'Link kopieren': 'Copy link',

  // --- Kalender, Aufgaben, Notizen ------------------------------------------
  'Der Kalender konnte nicht geladen werden.': 'The calendar could not be loaded.',
  'Voriger': 'Previous',
  'Nächster': 'Next',
  'Neuer Termin': 'New event',
  'In diesem Monat steht nichts an.': 'Nothing is coming up this month.',
  'Wiederholt sich': 'Repeats',
  'Nur für Beteiligte': 'Only for those involved',
  '„{name}" löschen?': 'Delete „{name}"?',
  'Konnte nicht gespeichert werden.': 'Could not be saved.',
  'Worum geht es?': 'What is it about?',
  'Wann?': 'When?',
  'Wo? (freiwillig)': 'Where? (optional)',
  'Wiederholt sich das?': 'Does it repeat?',
  'Einmalig': 'Once',
  'Täglich': 'Daily',
  'Wöchentlich': 'Weekly',
  'Monatlich': 'Monthly',
  'Jährlich': 'Yearly',
  'Nur für mich und die Eingeladenen sichtbar': 'Visible only to me and the people invited',
  'Speichern': 'Save',
  'Was ist zu tun?': 'What needs doing?',
  'Milch kaufen': 'Buy milk',
  'Hinzufügen': 'Add',
  'Erledigte anzeigen': 'Show completed',
  'bis {datum}': 'by {datum}',
  'für {name}': 'for {name}',
  'von {name}': 'by {name}',
  'Neue Notiz': 'New note',
  'Verschlüsselt': 'Encrypted',
  'Titel': 'Title',
  'Inhalt': 'Content',
  'Verschlüsseln (für Passwörter, Notfallinfos)': 'Encrypt (for passwords, emergency details)',
  'Der Inhalt wird auf deinem Gerät verschlüsselt. Der Server kann ihn nicht lesen — dafür findet die Suche diese Notiz nicht. Der Titel bleibt lesbar, damit du sie in der Liste wiederfindest.':
    'The content is encrypted on your device. The server cannot read it — in return, search will not find this note. The title stays readable so that you can find it again in the list.',
  'Diese Notiz kann der Server lesen. Dafür findest du sie über die Suche.':
    'The server can read this note. In return you can find it through search.',
  '[Konnte nicht entschlüsselt werden — vielleicht auf einem anderen Gerät angelegt?]':
    '[Could not be decrypted — perhaps it was created on another device?]',
  'Diese Notiz löschen?': 'Delete this note?',

  // --- Einstellungen --------------------------------------------------------
  'Was die App darf': 'What the app is allowed to do',
  'Nichts davon ist Pflicht. Aber ohne die Erlaubnis funktioniert der jeweilige Teil nicht — und die App sagt es dann nicht noch einmal.':
    'None of this is required. But without permission the part in question will not work — and the app will not say so again.',
  'Erlauben': 'Allow',
  'Blockiert. Die App kann nicht noch einmal fragen — das geht nur in den Browser-Einstellungen bei dieser Seite.':
    'Blocked. The app cannot ask again — that only works in the browser settings for this site.',
  'Normal': 'Normal',
  'Groß': 'Large',
  'Sehr groß': 'Very large',
  'Alles wächst mit: Knöpfe, Abstände, Zeilenhöhe. Nichts wird abgeschnitten.':
    'Everything grows with it: buttons, spacing, line height. Nothing gets cut off.',
  'Farben': 'Colours',
  'Wie das Gerät': 'Like the device',
  'Hell': 'Light',
  'Dunkel': 'Dark',
  'Sparsam': 'Frugal',
  'Automatisch': 'Automatic',
  'Ein': 'On',
  'Ohne Bewegung, ohne Schatten, und die Verschlüsselung wird erst geladen, wenn du den Chat öffnest. Auf einem älteren Telefon oder im Datensparmodus merkt man den Unterschied.':
    'No motion, no shadows, and the encryption only loads when you open the chat. On an older phone or in data saver mode you notice the difference.',
  'Ansicht': 'View',
  'Standard': 'Standard',
  'Einfach': 'Simple',
  'Einfach blendet Nebensachen aus: Reaktionen und das Aktionsmenü an Nachrichten, die Bildschirmfreigabe im Anruf. Schreiben, Anrufen, Standort, Status und SOS bleiben unverändert — und die Einstellungen findest du weiterhin hier.':
    'Simple hides the side matters: reactions and the action menu on messages, screen sharing during a call. Writing, calling, location, status and SOS stay exactly as they are — and you still find the settings here.',
  'Erlaubt': 'Allowed',
  'Blockiert': 'Blocked',
  'Noch nicht gefragt': 'Not asked yet',
  'Die App ist installiert. Sie startet ohne Browserleiste und bekommt Benachrichtigungen zuverlässiger.':
    'The app is installed. It starts without a browser bar and receives notifications more reliably.',
  'Auf den Startbildschirm legen': 'Put it on the home screen',
  'Dann startet die App wie jede andere — ohne Adressleiste, mit eigenem Symbol. Für SOS-Benachrichtigungen ist das der zuverlässigere Weg.':
    'Then the app starts like any other — no address bar, its own icon. For SOS notifications this is the more reliable way.',
  'Jetzt installieren': 'Install now',
  'Unten in der Leiste auf „Teilen" tippen (das Quadrat mit Pfeil)':
    'Tap „Share" in the bar at the bottom (the square with an arrow)',
  'Nach unten wischen zu „Zum Home-Bildschirm"': 'Swipe down to „Add to Home Screen"',
  'Oben rechts auf „Hinzufügen"': 'Tap „Add" at the top right',
  'Im Browser-Menü (⋮) findest du „App installieren" oder „Zum Startbildschirm hinzufügen".':
    'In the browser menu (⋮) you will find „Install app" or „Add to Home screen".',
  'Auf diesem Server sind Benachrichtigungen nicht eingerichtet. Wer ihn betreibt, findet die Anleitung in DEPLOYMENT.md.':
    'Notifications are not set up on this server. Whoever runs it will find the instructions in DEPLOYMENT.md.',
  'Das hat nicht geklappt. Vielleicht blockiert der Browser Benachrichtigungen.':
    'That did not work. Perhaps the browser is blocking notifications.',
  'Benachrichtigungen': 'Notifications',
  'Ohne sie erreicht dich ein SOS-Alarm erst, wenn du die App öffnest.':
    'Without them, an SOS alarm only reaches you once you open the app.',
  'Benachrichtigungen einschalten': 'Turn on notifications',
  'Ob eine Meldung einen Ton macht, stellt dein Telefon ein, nicht die App.': 'Whether an alert makes a sound is set by your phone, not by the app.',
  'Auf dem iPhone: Einstellungen → Mitteilungen → Family Hub → Töne. Und der Schalter an der Seite darf nicht auf lautlos stehen.': 'On iPhone: Settings → Notifications → Family Hub → Sounds. And the switch on the side must not be set to silent.',
  'Auf Android: Einstellungen → Apps → Benachrichtigungen. Dort lässt sich auch einstellen, dass ein Alarm den Ruhemodus durchbricht.': 'On Android: Settings → Apps → Notifications. There you can also allow an alarm to break through Do Not Disturb.',
  'Nur wenn du ihn aktiv teilst. Ohne Freigabe wird nichts gesendet.':
    'Only when you actively share it. Nothing is sent without your consent.',
  'Kamera': 'Camera',
  'Für Videoanrufe und zum Fotografieren.': 'For video calls and taking photos.',
  'Mikrofon': 'Microphone',
  'Für Anrufe.': 'For calls.',

  // --- Nachrichten entsperren (Satz und Passkey) ----------------------------
  'Die beiden Eingaben stimmen nicht überein.': 'The two entries do not match.',
  'Alte Nachrichten sind auf diesem Gerät danach nicht mehr lesbar.':
    'Old messages will no longer be readable on this device afterwards.',
  'Dein Konto, deine Fotos und alles andere bleiben. Auf einem anderen Gerät, das noch entsperrt ist, bleibt der Verlauf ebenfalls erhalten. Deine Familie sieht einen Hinweis, dass du ein neues Gerät benutzt — das ist so gewollt.':
    'Your account, your photos and everything else stay. On another device that is still unlocked, the history is kept as well. Your family sees a note that you are using a new device — that is intended.',
  'Gerät neu einrichten': 'Set up device afresh',
  'Nächstes Mal einfacher?': 'Easier next time?',
  'Mit Passkey einrichten': 'Set up with passkey',
  'Später': 'Later',
  'Deine Nachrichten schützen': 'Protect your messages',
  'Nachrichten entsperren': 'Unlock messages',
  'Leg deinen Finger auf, um deine Nachrichten zu öffnen.':
    'Place your finger to open your messages.',
  'Mit Passkey entsperren': 'Unlock with passkey',
  'Dein Satz': 'Your sentence',
  'Satz': 'Sentence',
  'zum Beispiel: mein blauer Stuhl knarzt': 'for example: my blue chair creaks',
  'Noch einmal': 'Once more',
  'Entsperren': 'Unlock',

  // --- Sozialer Anker und Notfall-Blatt -------------------------------------
  'Diese Person': 'This person',
  '{name} muss sich einmal anmelden, bevor sie Anteile verwahren kann.':
    '{name} has to sign in once before they can keep a share.',
  'Notfall-Hilfe durch die Familie': 'Emergency help from the family',
  'Du verwahrst einen Anteil für eine Person.': 'You keep one share for one person.',
  'Du verwahrst Anteile für {n} Personen.': 'You keep shares for {n} people.',
  'Wenn jemand Hilfe braucht, siehst du das hier.':
    'If somebody needs help, you will see it here.',
  'Eingerichtet. Deine drei Vertrauenspersonen wurden nicht informiert — sie erfahren erst davon, wenn du sie wirklich brauchst.':
    'Set up. Your three trusted people were not told — they only find out when you really need them.',
  'Wähle {n} Personen, denen du vertraust. Wenn du alle deine Geräte verlierst, können zwei davon gemeinsam dir wieder Zugang verschaffen.':
    'Choose {n} people you trust. If you lose all your devices, two of them together can give you access again.',
  'Deine {n} Vertrauenspersonen': 'Your {n} trusted people',
  'Dafür braucht ihr mindestens {n} Familienmitglieder. Lade zuerst weitere ein.':
    'For that you need at least {n} family members. Invite some more first.',
  'Einrichten': 'Set up',
  'Was das bedeutet': 'What this means',
  '{a} deiner {b} Vertrauenspersonen können gemeinsam an deine Nachrichten — auch ohne dich. Einzeln kann es niemand.':
    '{a} of your {b} trusted people can reach your messages together — even without you. On their own, nobody can.',
  'Damit das nicht heimlich passiert, dauert jede Wiederherstellung drei Tage. In dieser Zeit bekommst du auf allen deinen Geräten täglich eine Meldung und kannst sie mit einem Tippen abbrechen.':
    'So that this cannot happen in secret, every recovery takes three days. During that time you get a daily message on all your devices and can stop it with one tap.',
  'Wenn dir das zu weit geht: Richte den Anker nicht ein. Dein Notfall-Blatt reicht dann als einziger Rückweg — bewahre es gut auf.':
    'If that goes too far for you: do not set up the anchor. Your emergency sheet is then the only way back — keep it safe.',
  'Jemand versucht, Zugang zu deinen Nachrichten zu bekommen.':
    'Somebody is trying to get access to your messages.',
  '{a} von {b} Vertrauenspersonen haben zugestimmt.': '{a} of {b} trusted people have agreed.',
  'Freigabe am {datum}.': 'Release on {datum}.',
  'Warst du das nicht, brich es jetzt ab.': 'If that was not you, stop it now.',
  'Das war ich nicht — abbrechen': 'That was not me — stop it',
  'Noch nicht eingerichtet. Zwei Angehörige könnten dir dann gemeinsam wieder Zugang verschaffen.':
    'Not set up yet. Two relatives could then give you access again, together.',
  'Eingerichtet. Zwei deiner drei Vertrauenspersonen können dir gemeinsam wieder Zugang verschaffen.':
    'Set up. Two of your three trusted people can give you access again, together.',
  'Ein Anteil fehlt. Es reicht noch, aber richte den Anker bald neu ein.':
    'One share is missing. It is still enough, but set the anchor up again soon.',
  'Es sind zu wenige Anteile übrig — der Anker funktioniert nicht mehr. Bitte neu einrichten.':
    'Too few shares are left — the anchor no longer works. Please set it up again.',
  'Dein Notfall-Blatt': 'Your emergency sheet',
  'Diese {n} Wörter sind der einzige Weg zurück zu deinen alten Nachrichten, wenn du alle deine Geräte verlierst.':
    'These {n} words are the only way back to your old messages if you lose all your devices.',
  'Drucke sie aus oder schreibe sie ab': 'Print them out or copy them down',
  'und lege sie an einen sicheren Ort — zu den Ausweisen, nicht ins Handy.':
    'and put them somewhere safe — with your papers, not in your phone.',
  'Die Wörter sind englisch. Das ist Absicht: Sie stammen aus einer geprüften Liste, die eingebaute Rechtschreibprüfung merkt, wenn du dich vertippst. Du musst sie nicht verstehen, nur abschreiben.':
    'The words are English. That is on purpose: they come from a checked list, and the built-in spell check notices if you mistype. You do not have to understand them, only copy them.',
  'Ausdrucken': 'Print',
  'Kopieren': 'Copy',
  'Wir zeigen dir diese Wörter nur dieses eine Mal. Auch wir haben sie nicht — der Server kennt sie nicht und kann sie dir nicht noch einmal geben.':
    'We show you these words this one time only. We do not have them either — the server does not know them and cannot give them to you again.',
  'Ich habe die Wörter aufgeschrieben oder ausgedruckt.':
    'I have written the words down or printed them out.',
  'Jemand braucht deine Hilfe': 'Somebody needs your help',
  'möchte wieder Zugang zu den eigenen Nachrichten.':
    'would like access to their own messages again.',
  'Ruf die Person an': 'Call that person',
  'und frag nach dieser Zahl:': 'and ask for this number:',
  'Stimmt sie nicht, stimme nicht zu.': 'If it does not match, do not agree.',
  'Du hast bereits zugestimmt.': 'You have already agreed.',
  'Die Zustimmung selbst läuft über den Sicherheitsbereich auf deinem Gerät. Sie wird erst nach drei Tagen wirksam, und die betroffene Person kann jederzeit abbrechen.':
    'The agreement itself goes through the secure area on your device. It only takes effect after three days, and the person concerned can stop it at any time.',
  'Dafür müssen deine Nachrichten entsperrt sein. Melde dich neu an.':
    'For that your messages have to be unlocked. Sign in again.',
  'Ist das wirklich die richtige Person?': 'Is this really the right person?',
  'Wenn du sichergehen willst, dass niemand zwischen euch sitzt, kannst du eine Zahl vergleichen. Für den Alltag ist das nicht nötig.':
    'If you want to be sure that nobody is sitting between you, you can compare a number. For everyday use this is not necessary.',
  '{name} prüfen': 'Check {name}',
  '{name} hat sich noch nicht mit einem verschlüsselten Gerät angemeldet. Der Abgleich geht erst danach.':
    '{name} has not yet signed in with an encrypted device. The comparison only works after that.',
  'Die Nummer konnte nicht berechnet werden.': 'The number could not be calculated.',
  'Ist das wirklich {name}?': 'Is this really {name}?',
  'Ruf {name} an': 'Call {name}',
  'und lest euch diese Zahl gegenseitig vor. Stimmen beide überein, redet ihr wirklich miteinander und niemand sitzt dazwischen.':
    'and read this number out to each other. If both match, you really are talking to each other and nobody is in between.',
  'Sicherheitsnummer': 'Security number',
  'Die Zahl ist auf beiden Geräten gleich. Ändert sie sich später, hat {name} ein neues Gerät eingerichtet — dann fragt die App noch einmal nach.':
    'The number is the same on both devices. If it changes later, {name} has set up a new device — the app will then ask again.',
  'Geräte von {name}': 'Devices belonging to {name}',
  'Abgeglichen': 'Compared',
  'Noch nicht abgeglichen': 'Not compared yet',
  'Zahl stimmt überein': 'The number matches',

  // --- Standort-Banner ------------------------------------------------------
  'jemandem': 'somebody',
  'Dein Standort wird geteilt mit {namen}': 'Your location is shared with {namen}',
  'noch {n} Min.': '{n} min left',
  'bis du es stoppst': 'until you stop it',
  'Das haben deine Eltern eingestellt. Du kannst es nicht selbst ausschalten — aber du kannst sagen, dass du das nicht möchtest.':
    'Your parents set this up. You cannot switch it off yourself — but you can say that you do not want it.',
  'Standort öffnen': 'Open location',
  'Teilen beenden': 'Stop sharing',
  'Was heißt das?': 'What does that mean?',

  // --- Meldungen aus dem Unterbau (Netz, Krypto, Anhänge, Anruf) ------------
  'Gerade zu viele Anfragen. Bitte kurz warten.':
    'Too many requests right now. Please wait a moment.',
  'Der Server antwortet nicht. Gleich noch einmal versuchen.':
    'The server is not answering. Try again in a moment.',
  'Das war zu groß.': 'That was too big.',
  'Da ist etwas schiefgelaufen.': 'Something went wrong.',
  'Unerwartete Antwort vom Server.': 'Unexpected answer from the server.',
  'Keine Verbindung zum Server.': 'No connection to the server.',
  'Diese Unterhaltung ist verschlüsselt. Warte kurz, dein Gerät wird gerade aufgenommen.':
    'This conversation is encrypted. Wait a moment, your device is being added.',
  'Diese Unterhaltung ist noch nicht bereit.': 'This conversation is not ready yet.',
  'Aus der Zeit vor der Verschlüsselung': 'From the time before encryption',
  'Schlüssel für diese Unterhaltung fehlt': 'The key for this conversation is missing',
  'Schlüsselwechsel': 'Key change',
  'Das stimmt nicht. Versuch es noch einmal.': 'That is not right. Please try again.',
  'Für diesen Speicher ist der Passkey noch nicht eingerichtet.':
    'The passkey is not set up for this store yet.',
  'Dieser Weg ist für diesen Speicher nicht eingerichtet.':
    'This way in is not set up for this store.',
  'Dieses Gerät kann Fotos nicht für den Versand vorbereiten. Ohne das Entfernen der Ortsangabe wird kein Bild verschickt.':
    'This device cannot prepare photos for sending. Without removing the location, no image is sent.',
  'Dieses Bild konnte nicht gelesen werden.': 'This image could not be read.',
  'Das kann im Chat nicht mitgeschickt werden. Bilder, Videos und Tonaufnahmen gehen.':
    'That cannot be sent in the chat. Images, videos and voice recordings work.',
  'Diese Datei ist für den Chat zu groß. Über den Dateibereich geht sie.':
    'This file is too big for the chat. It works through the files section.',
  'Videos können Aufnahmeort und -zeit enthalten. Die lassen sich hier nicht entfernen — bitte bestätige, dass du es trotzdem senden willst.':
    'Videos can contain where and when they were recorded. That cannot be removed here — please confirm that you want to send it anyway.',
  'Für diese Datei gibt es kein Vorschaubild.': 'There is no preview image for this file.',
  'Die Datei ist nicht mehr verfügbar.': 'The file is no longer available.',
  'Die Datei konnte nicht geladen werden.': 'The file could not be loaded.',
  'Der Upload wurde abgebrochen.': 'The upload was cancelled.',
  'Die Verbindung ist abgebrochen. Die andere Seite ist nicht mehr erreichbar.':
    'The connection dropped. The other side can no longer be reached.',
  'Ein Verbindungsschritt ist fehlgeschlagen.': 'A connection step failed.',
  'Die Verbindung konnte nicht ausgehandelt werden.':
    'The connection could not be negotiated.',

  // --- SOS: die vier Sätze, die der Browser-Durchlauf gefunden hat ----------
  'Testalarm auslösen': 'Raise a test alarm',
  'Der Test läuft genau wie ein echter Alarm — die Familie sieht aber deutlich, dass es eine Übung ist. Gut, um es einmal auszuprobieren.':
    'The test runs exactly like a real alarm — but the family clearly sees that it is a drill. Good for trying it out once.',
  'Das war ein Versehen': 'That was a mistake',
  'Ich habe Hilfe erhalten': 'I have got help',
  'Auflegen': 'Hang up',
  '„Ich kümmere mich" stoppt die weiteren Alarmstufen. Beenden kann den Alarm nur {name} selbst.':
    '„I am on it" stops the further alarm levels. Only {name} can end the alarm.',

  // --- Klingeln und Alarm auf jedem Bildschirm ------------------------------
  'Anruf': 'Call',
  'Jemand aus deiner Familie ruft an.': 'Someone in your family is calling.',
  '🚨 SOS-Alarm': '🚨 SOS alarm',
  '🧪 Test-Alarm': '🧪 Test alarm',
  'Jemand aus deiner Familie braucht Hilfe.': 'Someone in your family needs help.',
  '{wer} probt einen Alarm': '{wer} is practising an alarm',
  'Ansehen': 'Look',
  'Dieses Gerät kann den Passkey dafür nicht nutzen. Bitte tippe es ein.':
    'This device cannot use the passkey for that. Please type it in.',
  'Dieses Gerät kann den Passkey dafür nicht nutzen. Es bleibt beim Eintippen.':
    'This device cannot use the passkey for that. Typing it stays.',
  'Das hat der Passkey nicht beantwortet. Bitte tippe es doch ein.':
    'The passkey did not answer that. Please type it in after all.',
  '{was} vergessen': '{was} forgotten',
  '{was} vergessen?': '{was} forgotten?',
  'Es gibt kein Zurücksetzen: Was du dir ausgedacht hast, ist nirgends gespeichert, auch nicht auf dem Server. Was bleibt, ist dieses Gerät neu einzurichten.':
    'There is no reset: what you came up with is stored nowhere, not on the server either. What remains is to set this device up again.',
  'Du kannst diesen Bildschirm künftig mit deinem Passkey öffnen — Gesicht oder Finger statt tippen. Alle zwei Wochen fragt die App trotzdem einmal nach, damit du es nicht vergisst.':
    'From now on you can open this screen with your passkey — face or finger instead of typing. Every two weeks the app still asks once, so that you do not forget it.',
  'Kurze Kontrolle': 'A quick check',
  'Alle zwei Wochen fragt die App einmal nach — nicht aus Misstrauen, sondern damit du es nicht vergisst. Wenn dein Telefon einmal weg ist, ist das hier der einzige Weg zurück zu deinen Nachrichten.':
    'Every two weeks the app asks once — not out of mistrust, but so that you do not forget it. If your phone is ever gone, this is the only way back to your messages.',
  'Womit möchtest du deine Nachrichten schützen?':
    'What would you like to protect your messages with?',
  'Eine PIN ist schnell getippt und schnell geraten. Wer dein Telefon in die Hand bekommt, hat es damit am leichtesten. Ein Satz aus vier Wörtern ist deutlich sicherer.':
    'A PIN is quick to type and quick to guess. Anyone who gets hold of your phone has the easiest time with it. A sentence of four words is considerably safer.',
  'Das hier schützt deine Nachrichten auf diesem Gerät — auch vor dem Server. Niemand kann es nachschlagen, auch wir nicht.':
    'This protects your messages on this device — from the server too. Nobody can look it up, we cannot either.',
  'Oder gib ein, womit du deine Nachrichten geschützt hast.':
    'Or enter what you protected your messages with.',
  'Gib ein, womit du deine Nachrichten geschützt hast.':
    'Enter what you protected your messages with.',
  'Verbergen': 'Hide',
  'Anzeigen': 'Show',
  'Stimmt': 'That is it',
  'Wenn du das vergisst und alle deine Geräte verlierst, sind deine alten Nachrichten weg — auch wir kommen dann nicht mehr daran. Im nächsten Schritt bekommst du dafür ein Notfall-Blatt.':
    'If you forget this and lose all your devices, your old messages are gone — we cannot get at them either. In the next step you will get an emergency sheet for that.',
  'Ich komme gerade nicht darauf': 'I cannot think of it right now',
  'Dann geht es heute mit dem Passkey weiter und die App fragt in drei Tagen wieder. Sieh bis dahin auf deinem Notfall-Blatt nach.':
    'Then it goes on with the passkey today and the app asks again in three days. Until then, look at your emergency sheet.',
  'PIN': 'PIN',
  'Dein Passwort': 'Your password',
  'Deine PIN': 'Your PIN',
  'mindestens 8 Zeichen': 'at least 8 characters',
  'mindestens 6 Ziffern': 'at least 6 digits',
  'Vier Wörter, die nur du in dieser Reihenfolge kennst. Am sichersten und am leichtesten zu merken.':
    'Four words that only you know in this order. The safest and the easiest to remember.',
  'Ein gewöhnliches Passwort. Sicher genug, wenn es lang ist und nirgends sonst benutzt wird.':
    'An ordinary password. Safe enough if it is long and used nowhere else.',
  'Nur Ziffern, schnell einzutippen. Der bequemste und zugleich der schwächste Weg.':
    'Digits only, quick to type. The most convenient and at the same time the weakest way.',
  'Eine PIN besteht nur aus Ziffern.': 'A PIN consists of digits only.',
  'Bitte mindestens {n} Ziffern.': 'At least {n} digits, please.',
  'Alle Ziffern gleich — das errät jemand sofort.':
    'All digits the same — someone guesses that straight away.',
  'Eine Zahlenreihe wie diese errät jemand sofort.':
    'A run of numbers like this one is guessed straight away.',
  'Bitte mindestens {n} Zeichen — ein kurzer Satz funktioniert gut.':
    'At least {n} characters, please — a short sentence works well.',
  'Bitte mindestens {n} Zeichen.': 'At least {n} characters, please.',
  'Dafür müssen deine Nachrichten entsperrt sein. Lade die App neu und melde dich an.':
    'For that your messages need to be unlocked. Reload the app and sign in.',
  'Ab jetzt fragt die App bei jedem Start nach.': 'From now on the app asks every time it starts.',
  'Ab jetzt genügt dein Gesicht oder dein Finger.':
    'From now on your face or your finger is enough.',
  'Deine Nachrichten liegen auf diesem Gerät verschlüsselt. Hier stellst du ein, wie du sie aufschließt.':
    'Your messages are stored encrypted on this device. Here you set how you unlock them.',
  'Womit du dich anmeldest': 'What you sign in with',
  'Das hier bleibt immer bestehen, auch wenn du den Passkey benutzt. Es ist dein Weg zurück, wenn das Telefon einmal weg ist.':
    'This always stays, even when you use the passkey. It is your way back if the phone is ever gone.',
  'Mit {was} einloggen': 'Sign in with {was}',
  'Ändern': 'Change',
  'Gespeichert. Ab dem nächsten Start gilt das Neue.':
    'Saved. From the next start the new one applies.',
  'Mit Passkey einloggen': 'Sign in with passkey',
  'Ist eingeschaltet: Die App öffnet sich, sobald dein Gesicht erkannt oder dein Finger gelesen wurde. Alle zwei Wochen fragt sie einmal nach dem Getippten.':
    'Switched on: the app opens as soon as your face has been recognised or your finger read. Every two weeks it asks once for the typed one.',
  'Wenn du das einschaltest, öffnet sich die App, sobald dein Gesicht erkannt oder dein Finger gelesen wurde. Alle zwei Wochen fragt sie trotzdem einmal nach dem Getippten.':
    'If you switch this on, the app opens as soon as your face has been recognised or your finger read. Every two weeks it still asks once for the typed one.',
  'Passkey ausschalten': 'Switch passkey off',
  'Passkey einschalten': 'Switch passkey on',
  'Nächste Kontrolle: {wann}': 'Next check: {wann}',
  'Dieses Gerät kann keine Passkeys. Es bleibt beim Eintippen.':
    'This device cannot do passkeys. Typing it stays.',
  'Das Alte gilt danach nicht mehr. Schreib dir das Neue auf, bevor du speicherst.':
    'The old one stops working afterwards. Write the new one down before you save.',
  'Das ist der einzige Weg zu deinen Nachrichten. Richte zuerst einen anderen ein.':
    'That is the only way to your messages. Set up another one first.',
  'Ändert die ganze App.': 'Changes the whole app.',
  'Übersetzen, wenn dieses Gerät es nicht kann': 'Translating when this device cannot',
  'Über euren Server': 'Via your server',
  'Gar nicht': 'Not at all',
  'Dieses Gerät hat keine eingebaute Übersetzung — die gibt es bisher nur in Chrome und Edge, auf dem iPhone fehlt sie. Damit deine Nachrichten trotzdem übersetzt ankommen, übersetzt euer eigener Server sie. Er sieht die Nachricht dabei einmal im Klartext, bevor sie verschlüsselt wird, und speichert sie nicht. Kein fremder Dienst ist beteiligt.':
    'This device has no built-in translation — so far it exists only in Chrome and Edge, and on the iPhone it is missing. So that your messages still arrive translated, your own server translates them. It sees the message once in the clear, before it is encrypted, and does not store it. No outside service is involved.',
  'Deine Nachrichten kommen bei den anderen nur in deiner Sprache an. Dafür bekommt der Server nie einen Chat-Text zu sehen.':
    'Your messages reach the others in your language only. In exchange, the server never gets to see any chat text.',
  'Älter als dieses Gerät in der Unterhaltung':
    'Older than this device has been in the conversation',
  // --- Profilbild und Name der App (Phase 19) --------------------------------
  'Dein Bild': 'Your picture',
  'Wie soll sie heißen?': 'What should it be called?',
  'Dein Bild sehen alle in deiner Familie — sonst niemand. Es wird auf diesem Gerät auf ein Quadrat zugeschnitten und verkleinert; Aufnahmeort und Kameradaten bleiben dabei zurück.':
    'Everyone in your family sees your picture — nobody else. It is cropped to a square and made smaller on this device; the place it was taken and the camera details stay behind.',
  'Damit kann die App nichts anfangen. Bitte ein Foto auswählen.':
    'The app cannot do anything with that. Please pick a photo.',
  'Das Bild konnte nicht gespeichert werden.': 'The picture could not be saved.',
  'Das Bild konnte nicht entfernt werden.': 'The picture could not be removed.',
  'Dieser Browser kann Bilder nicht zuschneiden. Auf einem neueren Gerät geht es.':
    'This browser cannot crop pictures. It works on a newer device.',
  'Wird gespeichert…': 'Saving…',
  'Anderes Bild wählen': 'Choose a different picture',
  'Bild wählen': 'Choose a picture',
  'Bild entfernen': 'Remove picture',
  'Name der App': 'Name of the app',
  'Steht oben auf jedem Bildschirm — bei allen in der Familie.':
    'Shown at the top of every screen — for everyone in the family.',
  'Der Name konnte nicht gespeichert werden.': 'The name could not be saved.',
  'Gespeichert.': 'Saved.',
  'Namen speichern': 'Save name',
  // --- Menü zu einer Person, Vorschau in der Liste ---------------------------
  'Was möchtest du mit {wer} tun?': 'What would you like to do with {wer}?',
  'Schließen': 'Close',
  'Anhang': 'Attachment',
  // --- Verpasste Anrufe ------------------------------------------------------
  'Verpasster Anruf von {wer}': 'Missed call from {wer}',
  '{n} verpasste Anrufe — {wer}': '{n} missed calls — {wer}',
  'Hinweis ausblenden': 'Dismiss',
  // --- Quantensichere Anrufe -------------------------------------------------
  "Dieses Gerät kann Anrufe nicht quantensicher verschlüsseln. Auf einem neueren Gerät geht es.":
    "This device cannot encrypt calls in a quantum-safe way. It works on a newer device.",
  "Die sichere Verschlüsselung des Anrufs ließ sich nicht einrichten.":
    "The call could not be set up with secure encryption.",
}
